import {
  Text,
  GreyGrey40,
  GreyGrey60,
  GreyGrey90,
  IndigoIndigo50,
  Primary13SemiBold,
  Primary13Regular,
  Primary20SemiBold,
  XYChart,
  XYChartTooltipTemplate,
  Box,
  Flex
} from '@sequencehq/core-components'
import { FilterSelectInput } from 'modules/Insights/common/FilterSelectInput/FilterSelectInput'
import { useCallback } from 'react'
import { useEventTypesWidget } from 'modules/Insights/widgets/EventTypesWidget/useEventTypesWidget'
import { WidgetLoading } from 'modules/Insights/common/WidgetLoading/WidgetLoading'
import { formatInteger } from '@sequencehq/utils'
import { format, formatDateRange } from '@sequencehq/utils/dates'

interface EventTypesWidgetProps {
  dateRange: {
    start: Date
    end: Date
  }
}

export const EventTypesWidget: React.FC<EventTypesWidgetProps> = ({
  dateRange
}) => {
  const { data, isFetching, total, unit, eventTypeField, unitTickValues } =
    useEventTypesWidget(dateRange)
  const tooltipFormat = useCallback(
    (value: number) => `${value.toLocaleString('en-US')} events`,
    []
  )

  if (isFetching || !data) {
    return <WidgetLoading />
  }

  return (
    <Box
      width="390px"
      height="291px"
      boxShadow="0 2px 4px 0 rgba(15, 23, 42, 0.08)"
      border={`1px solid ${GreyGrey40}`}
      borderRadius="lg"
      p={4}
    >
      <Flex gap={2}>
        <Flex flexGrow="1" flexDirection="column" gap={2}>
          <Text as={Flex} {...Primary13SemiBold} color={GreyGrey60} gap={1}>
            Total Events
          </Text>
          <Text {...Primary20SemiBold} color={GreyGrey90}>
            {formatInteger()(total)} events
          </Text>
          <Text {...Primary13Regular} color={GreyGrey60}>
            {formatDateRange({
              from: dateRange.start,
              to: dateRange.end
            })}
          </Text>
        </Flex>
        <Flex width="120px" alignSelf="baseline">
          <FilterSelectInput
            placeholder="Event type"
            value={eventTypeField.value}
            options={eventTypeField.options}
            onChange={eventTypeField.onChange}
          />
        </Flex>
      </Flex>
      <Box mt={4} height="170px" width="100%">
        <XYChart
          variant="line"
          dateFrom={format(dateRange.start, 'yyyy-MM-dd')}
          dateTo={format(dateRange.start, 'yyyy-MM-dd')}
          data={data}
          unitTickValues={unitTickValues}
          unit={unit}
          renderTooltip={({ date, value }: { date: string; value: number }) => (
            <XYChartTooltipTemplate>
              <Text {...Primary13SemiBold} color="gray.70">
                {format(date, 'EEE MMM d, yyyy')}
              </Text>
              <>
                <Text
                  as={Flex}
                  {...Primary13Regular}
                  color="gray.70"
                  alignItems="center"
                  gap={2}
                >
                  <Flex h={2} w={2} bg={IndigoIndigo50} borderRadius="full" />
                  {format(new Date(date), 'EEE MMM d, yyyy')}
                </Text>
                <Text {...Primary13SemiBold} color="gray.80">
                  {tooltipFormat(value)}
                </Text>
              </>
            </XYChartTooltipTemplate>
          )}
        />
      </Box>
    </Box>
  )
}
